import icn_line_chart from 'assets/images/icons/icn_line_chart.png';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';

import React from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import AuthTrendChart from 'components/AuthTrendChart';
import AuthDistributionChart from 'components/AuthDistributionChart';
import CircularLoader from 'components/CircularLoader';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 550,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = ({ threshold = 120 }) => (
  <Box>
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#D0D0D0', mb: 1 }}>
        Note: Bands are relative to the configurable Auth-Time Threshold (default: 120s), measured on Connect Time for Most Users (P95).
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good - P95 ≤ {threshold}s</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Authentication time is within the configured threshold. Most users complete login quickly.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning - {threshold}s {'<'} P95 ≤ {threshold * 2}s</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Authentication is moderately over the threshold. Users may notice delays during peak hours or on specific APs or SSIDs.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Identify the APs or SSIDs with the highest authentication time.</li>
        <li>Check uplink stability and DHCP/DNS response latency.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical - P95 > {threshold * 2}s</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Authentication is far over the threshold. Users may abandon login or retry repeatedly.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Verify the captive portal and authentication backend services for overload.</li>
        <li>Isolate the slowest segments or redistribute load.</li>
      </Box>
    </Box>
  </Box>
);

const MetricLabel = ({ label, value, unit = "", highlight = false, badge = null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, height: '24px' }}>
    <Typography sx={{ fontSize: '13px', color: '#1A202C', fontWeight: 'bold', mr: 1, lineHeight: 1 }}>
      {label}:
    </Typography>
    <Typography sx={{ fontSize: '13px', color: highlight ? '#4C6EF5' : '#4A5568', fontWeight: highlight ? 'bold' : 500, lineHeight: 1 }}>
      {value?.toLocaleString()}{unit}
    </Typography>
    {badge}
  </Box>
);

const StatusBadge = ({ type }) => {
  const isGood = type === 'Good';
  const isWarning = type === 'Warning';

  const colors = isGood
    ? { bg: '#E6FFFA', text: '#319795', border: '#B2F5EA' }
    : isWarning
      ? { bg: '#FFFBE6', text: '#D48806', border: '#FFE58F' }
      : { bg: '#FFF5F5', text: '#E53E3E', border: '#FEB2B2' };

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      bgcolor: colors.bg,
      color: colors.text,
      px: 1,
      height: '20px',
      borderRadius: '4px',
      ml: 1.5,
      border: `1px solid ${colors.border}`,
      boxSizing: 'border-box'
    }}>
      {isGood && <CheckCircleOutlineIcon sx={{ fontSize: '12px', mr: 0.5 }} />}
      <Typography sx={{ fontSize: '10px', fontWeight: 'bold', lineHeight: 1 }}>{type}</Typography>
    </Box>
  );
};

const CaptivePortalAuthTime = ({ chartData, isEmpty, onClick, loading = false }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (val) => {
    const threshold = chartData?.threshold ?? 120;
    if (val <= threshold) return 'Good';
    if (val <= threshold * 2) return 'Warning';
    return 'Critical';
  };

  const getStatusInfo = (val) => {
    const type = getStatus(val);
    if (type === 'Good') return { label: 'Good', color: '#4CAF50' };
    if (type === 'Warning') return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        pointerEvents: isEmpty ? 'none' : 'auto',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_line_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Captive Portal Authentication Time
          </Typography>
          <HtmlTooltip title={<TooltipContent threshold={chartData?.threshold} />} placement="auto">
            <Box component="img" src={icn_info_solid} sx={{ width: '16px', height: '16px', cursor: 'pointer' }} />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {
        loading ? (
          <Box sx={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
            <CircularLoader />
          </Box >
        ) : isEmpty ? (
          <Box sx={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
              }}>
              No data available
            </Typography>
          </Box>
        ) : (
          <>
            {/* Stats Section */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              mb: 4
            }}>
              <Box sx={{
                width: { xs: '100%', md: '35%' },
                pr: { xs: 0, md: 4 },
                mb: { xs: 3, md: 0 }
              }}>
                <Typography sx={{ color: '#718096', fontWeight: 'bold', mb: 1, display: 'block', fontSize: '13px' }}>
                  Status
                </Typography>
                <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: getStatusInfo(chartData.P95sec).color, lineHeight: 1.2 }}>
                  {getStatusInfo(chartData.P95sec).label}
                </Typography>
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', md: 'block' }, mx: 2, borderColor: '#EDF2F7' }}
              />

              <Box sx={{ flex: 1, pl: { xs: 0, md: 2 } }}>
                <MetricLabel
                  label="Connect Time for Most Users"
                  value={chartData.P95sec}
                  unit="s"
                  highlight
                />
                <MetricLabel label="Median" value={chartData.P50sec} unit="s" highlight />
                <MetricLabel label="Sessions" value={chartData.sessions} highlight />
              </Box>
            </Box>

            <Divider sx={{ mb: 4, borderColor: '#EDF2F7' }} />

            {/* Charts Section - 左右並排 */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1A202C', mb: 2 }}>
                  Authentication Time Distribution
                </Typography>
                <Box sx={{ height: '220px', width: '100%' }}>
                  <AuthDistributionChart data={chartData.distribution} loading={loading} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1A202C', mb: 2 }}>
                  Most Users Connect Time Trend
                </Typography>
                <Box sx={{ height: '220px', width: '100%' }}>
                  <AuthTrendChart data={chartData.series} loading={loading} />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
    </Box >
  );
};

export default CaptivePortalAuthTime;
